import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '@/api/generated/schema';
import { authStore } from '@/stores/auth-store';

/** Chemins dont l'appel ne doit JAMAIS déclencher de tentative de rafraîchissement. */
const NO_REFRESH_PATHS = ['/api/v1/auth/login', '/api/v1/auth/refresh', '/api/v1/auth/register'];

/**
 * Erreur métier renvoyée par l'API, au format RFC 9457.
 *
 * `code` est un identifiant STABLE (`auth.invalid_credentials`, `screen.not_paired`...) :
 * c'est lui que le code teste, jamais le message, qui peut être reformulé côté serveur
 * sans préavis. `errors` porte le détail par champ pour les erreurs de validation.
 */
export class ApiError extends Error {
  // Champs déclarés explicitement plutôt qu'en propriétés de constructeur : celles-ci sont
  // une syntaxe TypeScript sans équivalent JavaScript, interdite par `erasableSyntaxOnly`.
  // Cette option garantit que le code reste compilable par un simple effacement des types,
  // donc directement exécutable par Node ou par un bundler sans transformation.
  readonly status: number;
  readonly code: string;
  readonly errors: Record<string, string[]> | undefined;

  constructor(status: number, code: string, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.errors = errors;
  }

  /** Vrai si l'erreur porte un détail de validation exploitable par un formulaire. */
  get hasFieldErrors(): boolean {
    return this.errors !== undefined && Object.keys(this.errors).length > 0;
  }
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

/**
 * Rafraîchissement de session à VOL UNIQUE (« single flight »).
 *
 * Sans cette précaution, une page qui lance six requêtes en parallèle au chargement
 * déclencherait six rafraîchissements concurrents sur un access token expiré. Comme la
 * rotation de refresh token révoque le jeton précédent, les cinq derniers appels seraient
 * interprétés côté serveur comme une RÉUTILISATION de jeton — donc comme un vol — et
 * fermeraient toutes les sessions de l'utilisateur.
 *
 * Ici, le premier appelant lance le rafraîchissement, les suivants attendent la même
 * promesse.
 */
let refreshInFlight: Promise<boolean> | null = null;

/** Même base que le client typé, résolue paresseusement pour rester testable. */
function refreshBaseUrl(): string {
  return (
    import.meta.env.VITE_API_BASE_URL ??
    (typeof window === 'undefined' ? 'http://localhost' : window.location.origin)
  );
}

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await globalThis.fetch(`${refreshBaseUrl()}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        // Indispensable : c'est ce qui autorise l'envoi du cookie HttpOnly de
        // rafraîchissement avec la requête.
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        authStore.clear();

        return false;
      }

      const tokens = (await response.json()) as { accessToken: string };
      authStore.setSession(tokens.accessToken);

      return true;
    } catch {
      // Panne réseau : on ne vide PAS la session. L'utilisateur est peut-être simplement
      // hors ligne, le déconnecter lui ferait perdre son travail en cours.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = authStore.getAccessToken();

    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }

    return request;
  },

  async onResponse({ request, response }) {
    if (response.status !== 401) {
      return response;
    }

    const url = new URL(request.url);

    if (NO_REFRESH_PATHS.some((path) => url.pathname.startsWith(path))) {
      return response;
    }

    // `X-Token-Expired` est posé par l'API quand la cause du 401 est une EXPIRATION.
    // Un 401 pour toute autre raison (jeton falsifié, signature invalide) ne doit pas
    // déclencher de rafraîchissement : il n'y a rien à rafraîchir.
    if (response.headers.get('X-Token-Expired') !== 'true') {
      await clearIfSessionDead(request, response);

      return response;
    }

    if (!(await refreshSession())) {
      return response;
    }

    // Un seul réessai, avec le nouveau jeton. Pas de boucle : si ce réessai échoue
    // encore en 401, insister ne peut que produire une boucle infinie.
    const retry = request.clone();
    retry.headers.set('Authorization', `Bearer ${authStore.getAccessToken() ?? ''}`);

    const retried = await globalThis.fetch(retry);

    if (retried.status === 401) {
      await clearIfSessionDead(retry, retried);
    }

    return retried;
  },
};

/** Codes d'un 401 qui signifient « la session n'existe plus », et non « action refusée ». */
const DEAD_SESSION_CODES = ['auth.required', 'auth.session_stale'];

/**
 * Vide la session locale quand un 401 prouve qu'elle est morte côté serveur.
 *
 * Sans cela, un jeton rejeté (révoqué, signé par une clé tournée, compte supprimé) restait
 * en mémoire : la garde du routeur, qui ne regarde que sa présence, laissait naviguer
 * indéfiniment sur des pages dont toutes les requêtes échouaient.
 *
 * Tous les 401 ne sont pas concernés : `auth.invalid_credentials` (mauvais mot de passe
 * actuel) ou un défi 2FA invalide sont des refus MÉTIER, qui ne doivent pas déconnecter.
 * Le 401 du middleware JWT, lui, n'a pas de corps — donc pas de code.
 */
async function clearIfSessionDead(request: Request, response: Response): Promise<void> {
  if (!request.headers.has('Authorization')) {
    return;
  }

  let code: string | undefined;

  try {
    code = ((await response.clone().json()) as ProblemDetails).code;
  } catch {
    // Corps vide ou non JSON : 401 du middleware d'authentification.
  }

  if (code === undefined || DEAD_SESSION_CODES.includes(code)) {
    authStore.clear();
  }
}

const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok || response.status === 204) {
      return response;
    }

    let problem: ProblemDetails = {};

    try {
      problem = (await response.clone().json()) as ProblemDetails;
    } catch {
      // Réponse non JSON (502 d'un reverse proxy, page d'erreur HTML) : on garde un
      // message générique plutôt que de laisser fuir du HTML dans l'interface.
    }

    throw new ApiError(
      response.status,
      problem.code ?? `http.${response.status}`,
      problem.detail ?? problem.title ?? 'Une erreur inattendue est survenue.',
      problem.errors,
    );
  },
};

/**
 * Client HTTP typé, dérivé du document OpenAPI de l'API.
 *
 * Les chemins, paramètres, corps de requête et de réponse sont vérifiés à la COMPILATION :
 * renommer un champ côté C# casse le build du frontend au lieu de produire un `undefined`
 * silencieux en production. Le fichier de types est régénéré par `npm run api:sync`.
 */
/**
 * Base des URL d'API.
 *
 * Par défaut l'origine du document : l'API est servie sur le même domaine que le frontend
 * (via le proxy Vite en développement, via le reverse proxy en production). C'est une
 * CONDITION du fonctionnement du refresh token, qui est un cookie `SameSite=Strict` — sur
 * deux origines distinctes, le navigateur ne l'enverrait jamais.
 *
 * `VITE_API_BASE_URL` permet de surcharger cette base pour un déploiement où l'API vit sur
 * un autre domaine ; il faut alors ajuster la politique du cookie en conséquence.
 * Le repli sur une valeur absolue couvre les environnements sans `window` (tests, rendu
 * serveur éventuel) : `new Request()` refuse une URL relative hors navigateur.
 */
const baseUrl =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window === 'undefined' ? 'http://localhost' : window.location.origin);

export const api = createClient<paths>({
  baseUrl,
  credentials: 'include',

  // PAS de `Content-Type: application/json` ici : fixé au niveau du client, il serait
  // envoyé même sur les GET sans corps (openapi-fetch fusionne les en-têtes globaux
  // APRÈS sa propre règle « pas de Content-Type sans corps »), et FastEndpoints rejette
  // alors la requête en tentant de désérialiser un corps JSON vide (400 Bad Request).
  // openapi-fetch pose déjà ce header lui-même, correctement, sur les requêtes qui ont un
  // corps (POST/PUT avec `body`) — inutile et dangereux de le dupliquer ici.

  // `fetch` est résolu À CHAQUE APPEL et non capturé à la création du client. Sans cela,
  // openapi-fetch mémorise la référence globale au moment de l'import du module, ce qui
  // rend le client impossible à intercepter — en test comme derrière un outil de mesure
  // qui remplace `globalThis.fetch` après le chargement de l'application.
  fetch: (request) => globalThis.fetch(request),
});

// ORDRE D'ENREGISTREMENT — subtilité d'openapi-fetch à connaître :
// `onRequest` s'exécute dans l'ordre d'enregistrement, mais `onResponse` s'exécute dans
// l'ordre INVERSE. Pour que le rafraîchissement de session puisse intercepter un 401 AVANT
// que le middleware d'erreur ne lève une ApiError, l'authentification doit donc être
// enregistrée en DERNIER.
api.use(errorMiddleware);
api.use(authMiddleware);

/** Déclenche la restauration de session au démarrage de l'application. */
export async function bootstrapSession(): Promise<boolean> {
  return refreshSession();
}
